import React, { useState, useEffect, useRef } from 'react';
import contentService from '../services/contentService';
import './CommentsSection.css';
import Loading from './Loading';

const CommentsSection = ({ postId, users = [] }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [filters, setFilters] = useState({
    type: 'all',
    resolved: 'all'
  });

  const [newComment, setNewComment] = useState({
    type: 'general',
    content: '',
    parent: null
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [showUserMentions, setShowUserMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (postId) {
      loadComments();
    }
  }, [postId, filters]);

  const loadComments = async () => {
    try {
      setLoading(true);
      setError(null);

      const filterParams = {};
      if (filters.type !== 'all') filterParams.type = filters.type;
      if (filters.resolved !== 'all') filterParams.resolved = filters.resolved;

      const response = await contentService.getComments(postId, filterParams);

      let commentsData = [];
      if (Array.isArray(response.data)) {
        commentsData = response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        commentsData = response.data.results;
      }

      setComments(commentsData);
    } catch (error) {
      console.error('Error loading comments:', error);
      setError('Error al cargar comentarios');
      setComments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleContentChange = (e) => {
    const value = e.target.value;
    const cursorPosition = e.target.selectionStart;
    setNewComment({ ...newComment, content: value });

    const textBeforeCursor = value.substring(0, cursorPosition);
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');
    if (lastAtSymbol !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtSymbol + 1);
      if (!textAfterAt.includes(' ')) {
        setMentionSearch(textAfterAt);
        setShowUserMentions(true);
      } else {
        setShowUserMentions(false);
      }
    } else {
      setShowUserMentions(false);
    }
  };

  const insertMention = (username) => {
    const textarea = textareaRef.current;
    const cursorPosition = textarea.selectionStart;
    const textBeforeCursor = newComment.content.substring(0, cursorPosition);
    const textAfterCursor = newComment.content.substring(cursorPosition);
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');
    const beforeAt = textBeforeCursor.substring(0, lastAtSymbol);
    const newContent = `${beforeAt}@${username} ${textAfterCursor}`;
    setNewComment({ ...newComment, content: newContent });
    setShowUserMentions(false);
    textarea.focus();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('El archivo es demasiado grande. Máximo 10MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.content.trim()) {
      alert('Por favor escribe un comentario');
      return;
    }
    try {
      const commentData = {
        post: postId,
        comment_type: newComment.type,
        content: newComment.content.trim(),
        parent: newComment.parent
      };
      await contentService.createComment(commentData, selectedFile);
      setNewComment({ type: 'general', content: '', parent: null });
      setSelectedFile(null);
      setReplyingTo(null);
      setShowCommentForm(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadComments();
    } catch (error) {
      console.error('Error creating comment:', error);
      alert('Error al publicar comentario');
    }
  };

  const handleEditComment = async (commentId) => {
    if (!editingComment.content.trim()) {
      alert('El comentario no puede estar vacío');
      return;
    }
    try {
      await contentService.updateComment(
        commentId,
        { content: editingComment.content },
        editingComment.newFile
      );
      setEditingComment(null);
      loadComments();
    } catch (error) {
      console.error('Error updating comment:', error);
      alert('Error al actualizar comentario');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('¿Estás seguro de eliminar este comentario?')) return;
    try {
      await contentService.deleteComment(commentId);
      loadComments();
    } catch (error) {
      console.error('Error deleting comment:', error);
      alert(error.response?.data?.error || 'Error al eliminar comentario');
    }
  };

  const handleResolveComment = async (commentId, isResolved) => {
    try {
      if (isResolved) {
        await contentService.unresolveComment(commentId);
      } else {
        await contentService.resolveComment(commentId);
      }
      loadComments();
    } catch (error) {
      console.error('Error toggling resolve:', error);
      alert('Error al cambiar estado');
    }
  };

  const startReply = (comment) => {
    setReplyingTo(comment);
    setNewComment({ ...newComment, parent: comment.id, content: `@${comment.user_username} ` });
    setShowCommentForm(true);
    setTimeout(() => textareaRef.current?.focus(), 100);
  };

  const cancelReply = () => {
    setReplyingTo(null);
    setNewComment({ ...newComment, parent: null, content: '' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('es-CL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getCommentTypeLabel = (type) => {
    const labels = {
      general: 'General', brief: 'Brief', creative_idea: 'Idea Creativa',
      copy: 'Copy', content: 'Contenido', final: 'Final'
    };
    return labels[type] || type;
  };

  const filteredUsers = Array.isArray(users) ? users.filter(u =>
    u.username.toLowerCase().includes(mentionSearch.toLowerCase()) ||
    (u.full_name && u.full_name.toLowerCase().includes(mentionSearch.toLowerCase()))
  ) : [];

  const activeFilters = filters.type !== 'all' || filters.resolved !== 'all';

  if (loading) return <Loading fullScreen={false} size="sm" mensaje="Cargando" />;

  if (error) {
    return (
      <div className="cs-error">
        <p>{error}</p>
        <button onClick={loadComments} className="cs-btn-retry">Reintentar</button>
      </div>
    );
  }

  return (
    <div className="cs-wrapper">

      {/* Barra de acciones */}
      <div className="cs-toolbar">
        <button
          className={`cs-btn-toggle ${showFilters ? 'active' : ''} ${activeFilters ? 'has-active' : ''}`}
          onClick={() => setShowFilters(v => !v)}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 4h12M4 8h8M6 12h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          Filtros
          {activeFilters && <span className="cs-filter-dot" />}
        </button>

        <button
          className={`cs-btn-toggle ${showCommentForm ? 'active' : ''}`}
          onClick={() => {
            setShowCommentForm(v => !v);
            if (!showCommentForm) setTimeout(() => textareaRef.current?.focus(), 100);
          }}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          Agregar Comentario
        </button>
      </div>

      {/* Filtros — colapsan/expanden */}
      {showFilters && (
        <div className="cs-filters">
          <div className="cs-filter-group">
            <label>Tipo</label>
            <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
              <option value="all">Todos</option>
              <option value="general">General</option>
              <option value="brief">Brief</option>
              <option value="creative_idea">Idea Creativa</option>
              <option value="copy">Copy</option>
              <option value="content">Contenido</option>
              <option value="final">Final</option>
            </select>
          </div>
          <div className="cs-filter-group">
            <label>Estado</label>
            <select value={filters.resolved} onChange={(e) => setFilters({ ...filters, resolved: e.target.value })}>
              <option value="all">Todos</option>
              <option value="false">Pendientes</option>
              <option value="true">Resueltos</option>
            </select>
          </div>
          {activeFilters && (
            <button
              className="cs-btn-clear-filters"
              onClick={() => setFilters({ type: 'all', resolved: 'all' })}
            >
              Limpiar
            </button>
          )}
        </div>
      )}

      {/* Formulario nuevo comentario — colapsa/expande */}
      {showCommentForm && (
        <div className="cs-form-card">
          <div className="cs-form-header">
            <span className="cs-form-title">
              {replyingTo ? `Respondiendo a ${replyingTo.user_name}` : 'Nuevo comentario'}
            </span>
            <button className="cs-btn-close-form" onClick={() => { setShowCommentForm(false); cancelReply(); }}>
              ✕
            </button>
          </div>

          {replyingTo && (
            <div className="cs-replying-banner">
              <span>{replyingTo.content.substring(0, 60)}…</span>
              <button onClick={cancelReply} className="cs-btn-cancel-reply">✕</button>
            </div>
          )}

          <div className="cs-form-body">
            <select
              value={newComment.type}
              onChange={(e) => setNewComment({ ...newComment, type: e.target.value })}
              disabled={!!replyingTo}
              className="cs-select"
            >
              <option value="general">General</option>
              <option value="brief">Brief</option>
              <option value="creative_idea">Idea Creativa</option>
              <option value="copy">Copy</option>
              <option value="content">Contenido</option>
              <option value="final">Final</option>
            </select>

            <div className="cs-textarea-wrapper">
              <textarea
                ref={textareaRef}
                value={newComment.content}
                onChange={handleContentChange}
                placeholder="Escribe tu comentario… (usa @ para mencionar usuarios)"
                rows="4"
                className="cs-textarea"
              />
              {showUserMentions && filteredUsers.length > 0 && (
                <div className="cs-mentions-dropdown">
                  {filteredUsers.slice(0, 5).map(user => (
                    <div key={user.id} className="cs-mention-item" onClick={() => insertMention(user.username)}>
                      <span className="cs-mention-username">@{user.username}</span>
                      <span className="cs-mention-name">{user.full_name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="cs-file-row">
              <input
                ref={fileInputRef}
                type="file"
                id="comment-file"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <label htmlFor="comment-file" className="cs-btn-attach">
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                  <path d="M14 8l-5.5 5.5a4 4 0 01-5.657-5.657L8.5 2.186a2.5 2.5 0 013.535 3.535L6.379 11.38a1 1 0 01-1.414-1.414L10.5 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
                {selectedFile ? selectedFile.name : 'Adjuntar archivo'}
              </label>
              {selectedFile && (
                <button
                  className="cs-btn-remove-file"
                  onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                >✕</button>
              )}
            </div>

            <div className="cs-form-actions">
              <button onClick={handleAddComment} className="cs-btn-submit">
                {replyingTo ? 'Responder' : 'Publicar'}
              </button>
              <button onClick={() => { setShowCommentForm(false); cancelReply(); }} className="cs-btn-cancel">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lista de comentarios */}
      <div className="cs-list">
        {!Array.isArray(comments) || comments.length === 0 ? (
          <div className="cs-empty">
            <p>No hay comentarios aún</p>
            <p className="cs-empty-sub">Sé el primero en dejar un comentario</p>
          </div>
        ) : (
          comments.map(comment => (
            <CommentItem
              key={comment.id}
              comment={comment}
              onResolve={handleResolveComment}
              onDelete={handleDeleteComment}
              onEdit={setEditingComment}
              onReply={startReply}
              editingComment={editingComment}
              onSaveEdit={handleEditComment}
              onCancelEdit={() => setEditingComment(null)}
              formatDate={formatDate}
              getCommentTypeLabel={getCommentTypeLabel}
            />
          ))
        )}
      </div>
    </div>
  );
};

const CommentItem = ({
  comment, onResolve, onDelete, onEdit, onReply,
  editingComment, onSaveEdit, onCancelEdit,
  formatDate, getCommentTypeLabel, isReply = false
}) => {
  const isEditing = editingComment?.id === comment.id;

  return (
    <div className={`cs-comment ${isReply ? 'cs-reply' : ''} ${comment.is_resolved ? 'cs-resolved' : ''}`}>
      <div className="cs-comment-header">
        <div className="cs-comment-meta">
          <span className="cs-author">{comment.user_name}</span>
          <span className="cs-type-badge">{getCommentTypeLabel(comment.comment_type)}</span>
          {comment.updated_at !== comment.created_at && (
            <span className="cs-edited-badge">editado</span>
          )}
        </div>
        <span className="cs-date">{formatDate(comment.created_at)}</span>
      </div>

      {isEditing ? (
        <div className="cs-edit-form">
          <textarea
            value={editingComment.content}
            onChange={(e) => onEdit({ ...editingComment, content: e.target.value })}
            rows="3"
            className="cs-textarea"
          />
          <div className="cs-edit-actions">
            <button onClick={() => onSaveEdit(comment.id)} className="cs-btn-save-edit">Guardar</button>
            <button onClick={onCancelEdit} className="cs-btn-cancel-edit">Cancelar</button>
          </div>
        </div>
      ) : (
        <>
          <p className="cs-content">{comment.content}</p>

          {comment.mentioned_users_data && comment.mentioned_users_data.length > 0 && (
            <div className="cs-mentions">
              <span className="cs-mentions-label">Menciones:</span>
              {comment.mentioned_users_data.map(user => (
                <span key={user.id} className="cs-mention-tag">@{user.username}</span>
              ))}
            </div>
          )}

          {comment.attachment_url && (
            <div className="cs-attachment">
              <a href={comment.attachment_url} target="_blank" rel="noopener noreferrer" className="cs-attachment-link">
                {comment.attachment_name || 'Archivo adjunto'}
                {comment.attachment_size && (
                  <span className="cs-attachment-size">({(comment.attachment_size / 1024).toFixed(0)} KB)</span>
                )}
              </a>
            </div>
          )}
        </>
      )}

      <div className="cs-comment-actions">
        {!isReply && (
          <button onClick={() => onReply(comment)} className="cs-action-btn">Responder</button>
        )}
        {comment.can_edit && !isEditing && (
          <button onClick={() => onEdit({ id: comment.id, content: comment.content })} className="cs-action-btn">
            Editar
          </button>
        )}
        {comment.can_delete && (
          <button onClick={() => onDelete(comment.id)} className="cs-action-btn cs-action-danger">Eliminar</button>
        )}
        <button
          onClick={() => onResolve(comment.id, comment.is_resolved)}
          className={`cs-action-btn ${comment.is_resolved ? 'cs-action-resolved' : 'cs-action-resolve'}`}
        >
          {comment.is_resolved ? 'Reabrir' : 'Resolver'}
        </button>
      </div>

      {comment.is_resolved && (
        <div className="cs-resolved-status">
          Resuelto por {comment.resolved_by_name} · {formatDate(comment.resolved_at)}
        </div>
      )}

      {comment.replies && Array.isArray(comment.replies) && comment.replies.length > 0 && (
        <div className="cs-replies">
          <div className="cs-replies-header">
            {comment.replies.length} {comment.replies.length === 1 ? 'respuesta' : 'respuestas'}
          </div>
          {comment.replies.map(reply => (
            <CommentItem
              key={reply.id}
              comment={reply}
              onResolve={onResolve}
              onDelete={onDelete}
              onEdit={onEdit}
              onReply={onReply}
              editingComment={editingComment}
              onSaveEdit={onSaveEdit}
              onCancelEdit={onCancelEdit}
              formatDate={formatDate}
              getCommentTypeLabel={getCommentTypeLabel}
              isReply={true}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentsSection;
